'use client'

import { UnstyledModal } from '@/components/ui/general-modal'
import Image from 'next/image'
import { Fragment, memo, useCallback, useEffect, useId, useRef, useState } from 'react'
import { Download, EllipsisVertical, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/styles/cn'
import { useMeasure, useWindowSize } from 'react-use'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { motion } from 'framer-motion'
import { NodeItemContextProvider, useNodeItemContext } from '@/features/drive/entities/node/node-item-context'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuShortcut,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useWithUpperKeyboardShortcutsRestoration } from '@/features/keybindings/keybinding-module-context'
import { createImageUrl } from '@/utils/image/create-image-url'
import { run } from 'shared'
import { useFileContext } from '@/features/files/file-context'
import { AudioPlayerCard } from '@/features/drive/entities/node/node-preview/audio-player-card'
import { usePlayContext } from '@/features/play/play-context'
import { useNodeFileActions } from '@/features/drive/functionalities/actions/node/file/node-file-actions'
import { NodeItemContextMenu } from '@/features/drive/entities/node/node-item-context-menu'
import { NodeAction } from '@/features/drive/functionalities/actions/node/core/shared/utils/node-action-helpers'
import { useNodeActionHelpers } from '@/features/drive/functionalities/actions/node/core/shared/utils/use-node-action-helpers'
import { useDirectoryNavigationContext } from '@/features/drive/functionalities/navigation/directory-navigation-context'
import { useDownloadFiles } from '@/features/files/download-files'
import { NodeMoveAction } from '@/features/drive/functionalities/actions/node/core/node-move-action/node-move-action'
import { Video } from '@/components/ui/video'

export const NodeFilePreview = memo(() => {
    const { previewFile } = useDirectoryNavigationContext()

    if (!previewFile || previewFile.type !== 'FILE') return null

    return (
        <NodeItemContextProvider node={previewFile} index={0} isHighlighted={false}>
            <RawNodeFilePreview />
        </NodeItemContextProvider>
    )
})

NodeFilePreview.displayName = 'FilePreview'

const RawNodeFilePreview = () => {
    const componentId = useId()

    const { closePreviewFile } = useDirectoryNavigationContext()

    const onClose = useCallback(() => closePreviewFile(), [closePreviewFile])

    const { node } = useNodeItemContext()

    const fileContext = useFileContext()

    const { width, height } = useWindowSize()

    const [showToolbar, setShowToolbar] = useState(false)

    useEffect(() => {
        if (node && !showToolbar) {
            // Believe it or not, without posponing the initial render, the tooltip will just show up without hovering over the download button.
            setTimeout(() => {
                setShowToolbar(true)
            }, 0)
        }

        if (!node && showToolbar) {
            setShowToolbar(false)
        }
    }, [showToolbar, node])

    const [measureRef, { width: nodeWidth, height: nodeHeight }] = useMeasure<HTMLElement>()
    const [hasLoaded, setHasLoaded] = useState(false)

    const [isDropdownButtonHighlighted, setDropdownButtonHighlighted] = useState(false)
    const [isDropdownOpen, setDropdownOpen] = useState(false)

    const playRef = useRef<HTMLAudioElement | HTMLVideoElement | null>(null)

    const playContext = usePlayContext()

    useEffect(() => {
        return playContext.register(componentId, () => {
            if (playRef.current) {
                playRef.current.pause()
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentId])

    const refCallback = useCallback(
        (node: HTMLAudioElement | HTMLVideoElement | HTMLElement | undefined | null) => {
            if (node instanceof HTMLAudioElement || node instanceof HTMLVideoElement) {
                playRef.current = node
            } else {
                playRef.current = null
            }

            if (node) {
                measureRef(node)
            }
        },
        [measureRef]
    )

    useEffect(() => {
        if (isDropdownOpen) {
            setDropdownButtonHighlighted(true)
        } else {
            setTimeout(() => {
                setDropdownButtonHighlighted(false)
            }, 200)
        }
    }, [isDropdownOpen])

    const onEscape = useWithUpperKeyboardShortcutsRestoration((e: KeyboardEvent) => {
        e.preventDefault()
        onClose()
    })

    const [actionNodes, actions] = useNodeFileActions()

    const { getShouldShowAction } = useNodeActionHelpers()

    const downloadFiles = useDownloadFiles()

    return (
        <Fragment>
            {actionNodes}

            <UnstyledModal
                open
                onOpenChange={() => closePreviewFile()}
                title="File Preview"
                className="flex flex-col items-center justify-center overflow-hidden min-h-dvh min-w-full rounded-none"
                dialogContentProps={{
                    onClick: onClose,
                    onEscapeKeyDown: onEscape,
                }}
            >
                {showToolbar && node && (
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        className="absolute top-4 right-4 flex gap-4 justify-end items-center group w-fit"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            className={cn(
                                'w-fit py-2 px-2 transition-colors rounded-[10px] text-foreground gap-2 flex items-center',
                                'bg-background/60 group-hover:bg-background/60',
                                isDropdownButtonHighlighted && 'bg-background/60'
                            )}
                        >
                            {fileContext?.file && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="secondary"
                                            className="hover:bg-background group-hover:bg-background"
                                            onClick={(e) => {
                                                downloadFiles({ files: [fileContext.file] })
                                                e.stopPropagation()
                                            }}
                                        >
                                            <Download className="w-5 h-5" />
                                        </Button>
                                    </TooltipTrigger>

                                    <TooltipContent>Download</TooltipContent>
                                </Tooltip>
                            )}

                            <DropdownMenu open={isDropdownOpen} onOpenChange={setDropdownOpen}>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="secondary"
                                        className={cn(
                                            'hover:bg-background group-hover:bg-background',
                                            (isDropdownButtonHighlighted || isDropdownOpen) && 'bg-background'
                                        )}
                                        onClick={() => setDropdownOpen((prev) => !prev)}
                                    >
                                        <EllipsisVertical className="w-5 h-5" />
                                    </Button>
                                </DropdownMenuTrigger>

                                <DropdownMenuContent className={cn('w-48 mt-4', node.systemNode && 'w-64')}>
                                    {run(() => {
                                        return Object.entries(actions).map(([key, action]) => {
                                            if (!getShouldShowAction(action)) return null

                                            switch (key) {
                                                case NodeAction.MOVE: {
                                                    return <NodeMoveAction key={key} as="dropdown-menu" />
                                                }

                                                default: {
                                                    return (
                                                        <DropdownMenuItem key={key} onClick={action.call}>
                                                            {action.label}
                                                            <DropdownMenuShortcut>
                                                                <action.icon className="stroke-foreground h-3.5 w-3.5" />
                                                            </DropdownMenuShortcut>
                                                        </DropdownMenuItem>
                                                    )
                                                }
                                            }
                                        })
                                    })}
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <Separator className="w-7 rotate-90 bg-background/30" />

                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="secondary"
                                        className="bg-transparent shadow-none"
                                        onClick={onClose}
                                    >
                                        <X className="w-5 h-5" />
                                    </Button>
                                </TooltipTrigger>

                                <TooltipContent>Close</TooltipContent>
                            </Tooltip>
                        </div>
                    </motion.div>
                )}

                <div
                    className="flex flex-col gap-0 w-full max-w-7xl justify-center overflow-hidden p-0"
                    onClick={onClose}
                >
                    <div
                        style={{ height: `calc(100vh - 180px)` }}
                        className="flex min-w-full w-full flex-col justify-center items-center"
                        onClick={onClose}
                    >
                        <div
                            className="relative flex-1 bg-transparent w-full"
                            style={hasLoaded && nodeWidth ? { width: nodeWidth, height: nodeHeight } : {}}
                            onClick={onClose}
                        >
                            {/* <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                <LoadingSpinner size={40} colorClassName="stroke-background/60" strokeWidth={4} />
                            </div> */}

                            {/* PHOTO */}
                            {node?.file?.url && node?.file.category === 'IMAGE' && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: nodeWidth && hasLoaded ? 1 : 0 }}
                                    className="absolute inset-0 flex w-full items-center justify-center"
                                    onClick={onClose}
                                >
                                    <NodeItemContextMenu
                                        triggerClassName="h-full"
                                        actionNodes={undefined}
                                        actions={actions}
                                    >
                                        <Image
                                            src={createImageUrl(node.file?.url ?? '', {
                                                width: undefined,
                                                height: undefined,
                                            })}
                                            alt={node.uuid}
                                            height={height}
                                            width={width}
                                            style={{ height: '100%', width: 'unset' }}
                                            className="object-contain"
                                            priority
                                            onLoad={() => setHasLoaded(true)}
                                            ref={refCallback}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </NodeItemContextMenu>
                                </motion.div>
                            )}

                            {/* VIDEO */}
                            {node?.file?.url && node?.file.category === 'VIDEO' && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: nodeWidth && hasLoaded ? 1 : 0 }}
                                    className="absolute inset-0 flex w-full h-full min-h-full items-center justify-center"
                                    onClick={onClose}
                                >
                                    <NodeItemContextMenu actionNodes={undefined} actions={actions}>
                                        <Video
                                            ref={refCallback}
                                            src={node.file?.url}
                                            controls
                                            onCanPlay={(e) => {
                                                e.stopPropagation()
                                                setHasLoaded(true)
                                            }}
                                            onPlay={() => {
                                                playContext.onPlay(componentId)
                                            }}
                                            className="h-full min-h-full object-contain focus-within:ouline-none focus:outline-hidden absolute inset-0 mx-auto"
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </NodeItemContextMenu>
                                </motion.div>
                            )}
                            {/* CSV */}
                            {node?.file?.url && node?.file.type === 'csv' && (
                                <NodeItemContextMenu actionNodes={undefined} actions={actions}>
                                    <Button
                                        ref={refCallback}
                                        variant="primary"
                                        className="absolute inset-0 flex w-full items-center justify-center"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            if (node?.file) downloadFiles({ files: [node.file] })
                                        }}
                                    >
                                        Download
                                    </Button>
                                </NodeItemContextMenu>
                            )}
                            {/* AUDIO */}
                            {node?.file?.url && node?.file.category === 'AUDIO' && (
                                <NodeItemContextMenu actionNodes={undefined} actions={actions}>
                                    <AudioPlayerCard
                                        ref={refCallback}
                                        fileUrl={node.file.url}
                                        fileName={node.file.name}
                                        fileType={node.file.type}
                                        onCanPlay={() => setHasLoaded(true)}
                                        onPlay={() => {
                                            playContext.onPlay(componentId)
                                        }}
                                        onStopPropagation={(e) => e.stopPropagation()}
                                    />
                                </NodeItemContextMenu>
                            )}
                        </div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: nodeWidth && hasLoaded ? 1 : 0 }}
                            style={{ width: nodeWidth }}
                            className="flex items-center gap-4 mt-4 text-background text-[13px]"
                        >
                            {node.file?.name}
                        </motion.div>
                    </div>
                </div>
            </UnstyledModal>
        </Fragment>
    )
}
